import { useCallback, useContext, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { Form as FormAntd } from 'antd';
import { SearchOutlined } from '@ant-design/icons';

import { ActionButton } from '@hdi-seguros/web-components.action-button';
import {
  ColSize,
  ContentSection,
  Form,
  InputForm,
  RowSize,
  SelectForm,
} from '../../../../../../../shared/components';
import { ClientDataModal } from './components/ClientDataModal';

import {
  GenericDataFormContext,
  ProvincesInderCodes,
  QuotationInfoContext,
  StepOneFormContext,
} from '../../contexts';

import { QuotationStep } from '../../interfaces';

import { StepOneForm } from '../../../utils';
import { ValidateForm } from '../../../../../../../shared/utils';
import { QuotationStepButtons } from '../QuotationStepButtons';
import { LoadLegalRepresentative } from './components/LoadLegalRepresentative';
import { IvaConditionCode, PERSON_TYPE } from '../../../../../../../shared/interfaces';


export const ClientData = () => {
  const validateForm = new ValidateForm();

  const {
    clientDataForm,
    setClientSelected,
    setAdditionalDataClientSelected,
    setClientListModalIsOpen,
    setIsRepresentativeModal,
    setStepOneForm,
    stepOneForm,
  } = useContext(StepOneFormContext);
  const {
    documentTypeList,
    getLocalitiesPerProvince,
    isLoadingLocalities,
    ivaConditionList,
    provinceList,
    provinceListInder,
    setLocalities,
    typePersonList,
    getLocalitiesByProvinceFromState,
  } = useContext(GenericDataFormContext);
  const { setCurrentStep } = useContext(QuotationInfoContext);
  const { setQuotationInfo, savedQuotation, setSavedQuotation, setLoadSaveQuotationState, loadingRenewalQuotation, quotationToRenewal } = useContext(QuotationInfoContext);

  const isRenewal = loadingRenewalQuotation || quotationToRenewal;

  const navigate = useNavigate();

  const provinceSelected: string = FormAntd.useWatch(
    'province',
    clientDataForm
  );

  const ivaConditionSelected = FormAntd.useWatch('ivaCondition', clientDataForm);
  const isConsumidorFinalIva =
    String(ivaConditionSelected ?? '') === IvaConditionCode.CONSUMIDOR_FINAL;

  const cuitFieldRules = useMemo(
    () =>
      validateForm.generateRules({
        label: 'CUIT (Solo números, sin guiones)',
        rules: isConsumidorFinalIva
          ? ['minLength:11', 'maxLength:11', 'cuil']
          : ['required', 'minLength:11', 'maxLength:11', 'cuil'],
      }),
    [validateForm, isConsumidorFinalIva]
  );

  const localities = useMemo(
    () => getLocalitiesByProvinceFromState(provinceSelected),
    [provinceSelected, getLocalitiesByProvinceFromState]
  );

  const localitiesSelectorEnabled = useMemo(
    () => Boolean(localities?.length),
    [localities]
  );

  const handlerOnClick = async (formFields: StepOneForm) => {
    try {
      await clientDataForm.validateFields();

      delete formFields[''];

      setStepOneForm(formFields);
      const client = {
        nroAsegurado: formFields.nroAsegurado,           
        tipoDocumento: formFields.documentType,
        nroDocumento: formFields.documentNumber || '0',
        nombre: formFields.fullName,
        cuit: String(formFields.cuit || 0),
        tipoPersona: formFields.personType,
        codProvincia: formFields.province,
        codProvinciaInder: formFields.codProvinciaInder,
        codigoPostal: formFields.codigoPostal,
        sufijoCodigoPostal: formFields.sufijoCodigoPostal,
        condicionIVA: formFields.ivaCondition
      };

      const additionalData = {
        domicilio: formFields.address,
        codigoNacionalidad: formFields.nationalityCode,
        codigoSexo: formFields.sexCode,
        codigoTipoSociedad: formFields.companyTypeCode,
        fechaContrato: formFields.contractDate,
        fechaInscripcion: formFields.inscriptionDate,
        numeroInscripcion: formFields.inscriptionNumber,
        agentePercepcion: formFields.perceptionAgent
      };
      
      setClientSelected(client);

      setAdditionalDataClientSelected(additionalData);

      setCurrentStep(QuotationStep.TWO);
    } catch (error) {}
  };

  const handleCloseModal = () => {
    setClientListModalIsOpen(true);
    setIsRepresentativeModal(false);
  }

  const onFinish = async (formFields: StepOneForm) => {
    await handlerOnClick(formFields);
    clientDataForm.resetFields();
  };

  const onSelectProvince = async (provinceCode: string) => {
    clientDataForm.setFieldValue('codProvinciaInder', provinceListInder!.find((province: ProvincesInderCodes) => province.codigoProvincia === provinceCode)?.codProvinciaInder);
    clientDataForm.resetFields(['location']);
    setLocalities([]);
  };

  const onPressBackButton = useCallback(() => {
    if(savedQuotation) {
      navigate('/cotizador/nueva-cotizacion');
      setSavedQuotation(undefined);
      setLoadSaveQuotationState(undefined);
    };
    
    setQuotationInfo(undefined);
  }, [
    setQuotationInfo,
    savedQuotation,
    setSavedQuotation,
    setLoadSaveQuotationState
  ])

  useEffect(() => {
    if (stepOneForm) {
      for (let key in stepOneForm) {
        clientDataForm.setFieldValue(key, stepOneForm[key]);
      }
    }
    /* eslint-disable-next-line */
  }, []);

  useEffect(() => {
    if (provinceSelected?.length) getLocalitiesPerProvince(provinceSelected);
  }, [getLocalitiesPerProvince, provinceSelected]);

  return (
    <>
      <ClientDataModal />

      <Form
        formProps={{
          form: clientDataForm,
          name: 'insured-registration-form',
          onFinish: onFinish,
        }}
      >
        <InputForm
          formProps={{
            label: 'Código Postal:',
            name: 'codigoPostal',
            hidden: true
          }}
        />
        <InputForm
          formProps={{
            label: 'Número Asegurado:',
            name: 'nroAsegurado',
            initialValue: '0',
            hidden: true
          }}
        />
        <InputForm
          formProps={{
            label: 'Sufijo Código Postal:',
            name: 'sufijoCodigoPostal',
            hidden: true
          }}
        />
        <InputForm
          formProps={{
            label: 'Código Provincia:',
            name: 'codProvinciaInder',
            hidden: true
          }}
        />
        <ContentSection level='h3' title='Datos del cliente'>
          <RowSize>
            <ColSize size={50}>
              <SelectForm
                formProps={{
                  label: 'Tipo de Persona:',
                  name: 'personType',
                  rules: validateForm.generateRules({
                    label: 'tipo de persona',
                    rules: ['required'],
                  }),
                }}
                selectProps={{
                  loading: !typePersonList,
                  options: typePersonList,
                  disabled: isRenewal,
                }}
              />
            </ColSize>

            <ColSize size={50}>
              <SelectForm
                formProps={{
                  label: 'IVA:',
                  name: 'ivaCondition',
                  rules: validateForm.generateRules({
                    label: 'iva',
                    rules: ['required'],
                  }),
                }}
                selectProps={{
                  options: ivaConditionList,
                  loading: !ivaConditionList,
                  disabled: isRenewal,
                }}
              />
            </ColSize>

            <ColSize size={100}>
              <InputForm
                formProps={{
                  label: 'Apellido y Nombre:',
                  name: 'fullName',
                  rules: validateForm.generateRules({
                    label: 'apellido y nombre',
                    rules: ['required'],
                  }),
                }}
                inputProps={{ placeholder: 'Apellido y nombre', disabled: isRenewal}}
              />
            </ColSize>

            <ColSize size={25}>
              <SelectForm
                formProps={{
                  label: 'Tipo de Documento:',
                  name: 'documentType',
                  rules: [
                    {
                      validator: (_, value) => {
                        const personType = clientDataForm.getFieldValue('personType');
                        if (personType === PERSON_TYPE.FISICA.codigo && !value) {
                          return Promise.reject('El tipo de documento es obligatorio para persona física');
                        }
                        return Promise.resolve();
                      },
                    },
                  ],
                }}
                selectProps={{
                  loading: !documentTypeList,
                  options: documentTypeList,
                  disabled: isRenewal,
                }}
              />
            </ColSize>

            <ColSize size={25}>
              <InputForm
                formProps={{
                  label: 'Número de Documento:',
                  name: 'documentNumber',
                }}
                inputProps={{ placeholder: 'Número de documento', maxLength: 8, disabled: isRenewal  }}
              />
            </ColSize>

            <ColSize size={25}>
              <InputForm
                formProps={{
                  label: 'CUIT:',
                  name: 'cuit',
                  rules: cuitFieldRules,
                }}
                inputProps={{ placeholder: 'CUIT (Solo números, sin guiones)' , disabled: isRenewal}}
              />
            </ColSize>

            <ColSize size={25}>
              <SelectForm
                formProps={{
                  label: 'Provincia:',
                  name: 'province',
                  rules: validateForm.generateRules({
                    label: 'provincia',
                    rules: ['required'],
                  }),
                }}
                selectProps={{
                  showSearch: true,
                  loading: !provinceList,
                  onSelect: (value) => {
                    onSelectProvince(value);
                  },
                  options: provinceList,
                  disabled: isRenewal,
                }}
              />
            </ColSize>

            <ColSize size={25}>
              <SelectForm
                formProps={{
                  label: 'Localidad:',
                  name: 'location',
                  rules: validateForm.generateRules({
                    label: 'localidad',
                    rules: ['required'],
                  }),
                }}
                selectProps={{
                  disabled: isRenewal || !localitiesSelectorEnabled,
                  loading: isLoadingLocalities,
                  options: localities,
                  showSearch: true,
                  onChange: (value) => {
                    clientDataForm.setFieldValue('codigoPostal', value.slice(1));
                    clientDataForm.setFieldValue('sufijoCodigoPostal', value.slice(0, 1));
                  }
                }}
              />
            </ColSize>

            <ColSize size={25}>
              <FormAntd.Item label=' '>
                <ActionButton
                  className='btn-accent-outline-barbuss'
                  type='secondary'
                  label='Buscar cliente'
                  icon={<SearchOutlined />}
                  onClick={() => handleCloseModal()}
                  style={{ marginRight: 15, width: 165, height: 32 }}
                  disabled={isRenewal}
                />
              </FormAntd.Item>
            </ColSize>
            <ColSize size={25}>
              <InputForm
                formProps={{
                  label: '',
                  name: 'address',
                  hidden: true,
                }}
              />
            </ColSize>
            <ColSize size={25}>
              <InputForm
                formProps={{
                  label: '',
                  name: 'nationalityCode',
                  hidden: true,
                }}
              />
            </ColSize>
             <ColSize size={25}>
              <InputForm
                formProps={{
                  label: '',
                  name: 'sexCode',
                  hidden: true,
                }}
              />
            </ColSize>
             <ColSize size={25}>
              <InputForm
                formProps={{
                  label: '',
                  name: 'perceptionAgent',
                  hidden: true,
                }}
              />
            </ColSize>
            <ColSize size={25}>
              <InputForm
                formProps={{
                  label: '',
                  name: 'companyTypeCode',
                  hidden: true,
                }}
              />
            </ColSize>
            <ColSize size={25}>
              <InputForm
                formProps={{
                  label: '',
                  name: 'inscriptionNumber',
                  hidden: true,
                }}
              />
            </ColSize>
            <ColSize size={25}>
              <InputForm
                formProps={{
                  label: '',
                  name: 'inscriptionDate',
                  hidden: true,
                }}
              />
            </ColSize>
            <ColSize size={25}>
              <InputForm
                formProps={{
                  label: '',
                  name: 'contractDate',
                  hidden: true,
                }}
              />
            </ColSize>
          </RowSize>
        </ContentSection>

        <LoadLegalRepresentative />

        <QuotationStepButtons onPressBackButton={onPressBackButton} />
      </Form>
    </>
  );
};
